import { Link, usePage } from "@inertiajs/react"
import {
  LayoutDashboard,
  Calculator,
  CreditCard,
  Wallet,
  Landmark,
  Target,
  BarChart3,
  User,
  Sparkles,
} from "lucide-react"
import { cn } from "@/lib/utils"
import AppLogo from "@/components/app-logo"
import { BRAND_GRADIENT_CLASS } from "@/lib/color-constants"

const navigation = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    description: "Your FI overview",
  },
  {
    name: "FIRE Calculator",
    href: "/fi_calculator",
    icon: Calculator,
    description: "Plan your number & timeline",
  },
  {
    name: "Income",
    href: "/incomes",
    icon: Wallet,
    description: "Log paychecks & other income",
  },
  {
    name: "Expenses",
    href: "/expenses",
    icon: CreditCard,
    description: "Track spending by category",
  },
  {
    name: "Liabilities",
    href: "/liabilities",
    icon: Landmark,
    description: "Debts that reduce your net worth",
  },
  {
    name: "Goals",
    href: "/goals",
    icon: Target,
    description: "Custom savings targets",
  },
  {
    name: "Portfolios",
    href: "/portfolios",
    icon: BarChart3,
    description: "Investments & dividend income",
  },
]

const settingsNavigation = [
  { name: "Profile", href: "/settings/profile", icon: User },
]

export function AppSidebar() {
  const { url } = usePage()

  return (
    <aside className="border-sidebar-border/80 bg-sidebar text-sidebar-foreground fixed inset-y-0 left-0 z-20 flex w-24 shrink-0 flex-col border-r lg:w-80 lg:overflow-y-auto">
      <div className="border-sidebar-border/80 border-b px-6 py-6">
        <Link href="/" className="flex items-center">
          <AppLogo />
        </Link>

        <div className="mt-6 hidden rounded-[24px] border border-white/8 bg-white/6 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] lg:block">
          <div className="text-sidebar-foreground/60 flex items-center gap-2 text-[0.72rem] font-semibold tracking-[0.22em] uppercase">
            <Sparkles className="h-3.5 w-3.5" />
            Wealth System
          </div>
          <p className="text-sidebar-foreground/82 mt-3 text-sm leading-6">
            Track spending, measure FI progress, and monitor dividend income in
            one operating system.
          </p>
        </div>
      </div>

      <div className="text-sidebar-foreground/45 hidden px-4 pt-5 text-[0.72rem] font-semibold tracking-[0.22em] uppercase lg:block">
        Core Workspace
      </div>
      <nav className="flex-1 space-y-1.5 px-4 py-4">
        {navigation.map((item) => {
          const isActive = url.startsWith(item.href)
          return (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                "group flex items-center justify-center rounded-2xl px-3 py-3 text-sm font-medium transition-all duration-200 lg:justify-start lg:px-4",
                isActive
                  ? `${BRAND_GRADIENT_CLASS} text-white shadow-lg shadow-black/10`
                  : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/6",
              )}
            >
              <span
                className={cn(
                  "mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors",
                  isActive
                    ? "border-black/8 bg-black/8 text-slate-950"
                    : "text-sidebar-foreground/75 group-hover:text-sidebar-foreground border-white/8 bg-white/5",
                )}
              >
                <item.icon className="h-4 w-4" />
              </span>
              <span className="hidden flex-col lg:flex">
                <span>{item.name}</span>
                <span
                  className={cn(
                    "text-xs font-normal",
                    isActive ? "text-white/75" : "text-sidebar-foreground/45",
                  )}
                >
                  {item.description}
                </span>
              </span>
            </Link>
          )
        })}
      </nav>

      <div className="border-sidebar-border/80 border-t px-4 py-4">
        <div className="text-sidebar-foreground/45 hidden px-2 pb-3 text-[0.72rem] font-semibold tracking-[0.22em] uppercase lg:block">
          Account
        </div>
        <nav className="space-y-1">
          {settingsNavigation.map((item) => {
            const isActive = url.startsWith(item.href)
            return (
              <Link
                key={item.name}
                href={item.href}
                className={cn(
                  "group flex items-center justify-center rounded-2xl px-3 py-3 text-sm font-medium transition-all duration-200 lg:justify-start lg:px-4",
                  isActive
                    ? "text-sidebar-foreground bg-white/10"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/6",
                )}
              >
                <span className="text-sidebar-foreground/75 mr-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/8 bg-white/5">
                  <item.icon className="h-4 w-4" />
                </span>
                <span className="hidden lg:inline">{item.name}</span>
              </Link>
            )
          })}
        </nav>
      </div>
    </aside>
  )
}
